import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "./Meetings.css";
import AlertModal from "../AlertModal/AlertModal";
import config from "../../config";
import useAuth from "../../Auth/useAuth";

// API endpoints
const getMeetingsUrl = `${config.BASE_URL}/list-meetings`;
const addMeetingUrl = `${config.BASE_URL}/add-meeting`;
const updateMeetingUrl = `${config.BASE_URL}/update-meeting`;
const deleteMeetingUrl = `${config.BASE_URL}/delete-meeting`;
const getUsersUrl = `${config.BASE_URL}/list-users`;

const Meetings = () => {
  const { username, userProfile, setActiveModule } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [meetings, setMeetings] = useState([]);
  const [users, setUsers] = useState([]);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);

  // Form state
  // IMPORTANT:
  // participants remain usernames in the frontend form.
  // Backend converts them to { username, email }.
  const [formData, setFormData] = useState({
    meetingAgenda: "",
    dateTime: "",
    participants: [],
    description: "",
    createdBy: username || "",
  });

  const [participantInput, setParticipantInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const suggestionRef = useRef(null);

  // Delete modal state
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    recordId: null,
    meetingAgenda: "",
  });

  // Alert modal state
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
  });

  // Only Admin and Accountant can manage meetings
  const canManage = userProfile === "admin" || userProfile === "accountant";

  // ============================================================
  // MODAL
  // ============================================================

  const showModal = (title, message, type = "info") => {
    setModalConfig({
      isOpen: true,
      title,
      message,
      type,
    });
  };

  const closeModal = () => {
    setModalConfig((prev) => ({
      ...prev,
      isOpen: false,
    }));
  };

  // ============================================================
  // HELPER - Convert DB Date -> datetime-local value
  // ============================================================

  const formatDateTimeLocal = (dateValue) => {
    if (!dateValue) return "";

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    // datetime-local expects local browser time:
    // YYYY-MM-DDTHH:mm
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // ============================================================
  // HELPER - Convert datetime-local -> ISO
  // ============================================================

  const convertToISOString = (dateTimeLocal) => {
    if (!dateTimeLocal) return null;

    const date = new Date(dateTimeLocal);

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return date.toISOString();
  };

  // ============================================================
  // OPEN CREATE MODAL
  // ============================================================

  const openCreateModal = () => {
    if (!canManage) {
      showModal(
        "Access Denied",
        "Only Admin and Accountant can create meetings.",
        "error",
      );
      return;
    }

    setIsEditMode(false);
    setEditId(null);

    setFormData({
      meetingAgenda: "",
      dateTime: "",
      participants: [],
      description: "",
      createdBy: username || "",
    });

    setParticipantInput("");
    setSuggestions([]);
    setShowSuggestions(false);

    setIsModalOpen(true);
  };

  // ============================================================
  // OPEN EDIT MODAL
  // ============================================================

  const openEditModal = (meeting) => {
    if (!canManage) {
      showModal(
        "Access Denied",
        "Only Admin and Accountant can edit meetings.",
        "error",
      );
      return;
    }

    /*
     * Backend now returns:
     *
     * participants: [
     *   {
     *     username: "Shree",
     *     email: "shree@gmail.com"
     *   }
     * ]
     *
     * But our form uses:
     *
     * participants: ["Shree"]
     *
     * Therefore convert objects -> usernames.
     */

    const participantUsernames = (meeting.participants || [])
      .map((participant) => {
        if (typeof participant === "string") {
          return participant;
        }

        return participant?.username;
      })
      .filter(Boolean);

    setIsEditMode(true);
    setEditId(meeting._id);

    setFormData({
      meetingAgenda: meeting.meetingAgenda || "",
      dateTime: formatDateTimeLocal(meeting.dateTime),
      participants: participantUsernames,
      description: meeting.description || "",
      createdBy: meeting.createdBy || username || "",
    });

    setParticipantInput("");
    setSuggestions([]);
    setShowSuggestions(false);

    setIsModalOpen(true);
  };

  // ============================================================
  // CLOSE FORM MODAL
  // ============================================================

  const closeFormModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setEditId(null);

    setFormData({
      meetingAgenda: "",
      dateTime: "",
      participants: [],
      description: "",
      createdBy: username || "",
    });

    setParticipantInput("");
    setSuggestions([]);
    setShowSuggestions(false);
  };

  // ============================================================
  // FETCH DATA
  // ============================================================

  useEffect(() => {
    fetchUsers();
    fetchMeetings();

    sessionStorage.setItem("actMod", "meetings");
    setActiveModule(sessionStorage.getItem("actMod"));
  }, [username, setActiveModule]);

  // ============================================================
  // FETCH USERS
  // ============================================================

  const fetchUsers = async () => {
    try {
      const response = await axios.get(getUsersUrl, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        },
      });

      console.log("Fetched users:", response.data);

      if (Array.isArray(response.data)) {
        setUsers(response.data);
      } else if (response.data?.users && Array.isArray(response.data.users)) {
        setUsers(response.data.users);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    }
  };

  // ============================================================
  // FETCH MEETINGS
  // ============================================================

  const fetchMeetings = async () => {
    setIsLoading(true);

    try {
      const response = await axios.get(getMeetingsUrl, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        },
        params: {
          username,
        },
      });

      console.log("Fetched meetings response:", response.data);

      if (Array.isArray(response.data)) {
        setMeetings(response.data);
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        setMeetings(response.data.data);
      } else {
        setMeetings([]);
      }
    } catch (error) {
      console.error("Error fetching meetings:", error);

      showModal(
        "Error",
        error.response?.data?.message || "Failed to fetch meetings.",
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================================
  // PARTICIPANT MANAGEMENT
  // ============================================================

  const handleParticipantInputChange = (e) => {
    const value = e.target.value;

    setParticipantInput(value);

    if (value.length > 0 && Array.isArray(users) && users.length > 0) {
      const searchValue = value.toLowerCase();

      const filtered = users.filter((user) => {
        // Prevent selecting same participant twice
        const alreadySelected = formData.participants.includes(user.username);

        if (alreadySelected) {
          return false;
        }

        const usernameMatch = user.username
          ?.toLowerCase()
          .includes(searchValue);

        const fullNameMatch =
          user.fullName && user.fullName.toLowerCase().includes(searchValue);

        return usernameMatch || fullNameMatch;
      });

      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const addParticipant = (user) => {
    if (!user?.username) {
      return;
    }

    if (!formData.participants.includes(user.username)) {
      setFormData((prev) => ({
        ...prev,
        participants: [...prev.participants, user.username],
      }));
    }

    setParticipantInput("");
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const removeParticipant = (usernameToRemove) => {
    setFormData((prev) => ({
      ...prev,
      participants: prev.participants.filter((p) => p !== usernameToRemove),
    }));
  };

  // ============================================================
  // FORM HANDLERS
  // ============================================================

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ============================================================
  // SUBMIT
  // ============================================================

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!canManage) {
      showModal(
        "Access Denied",
        "Only Admin and Accountant can manage meetings.",
        "error",
      );
      return;
    }

    if (
      !formData.meetingAgenda.trim() ||
      !formData.dateTime ||
      formData.participants.length === 0
    ) {
      showModal(
        "Validation Error",
        "Please fill in all required fields and add at least one participant.",
        "error",
      );
      return;
    }

    /*
     * IMPORTANT:
     *
     * datetime-local gives:
     *
     * 2026-09-10T10:00
     *
     * We convert it to:
     *
     * 2026-09-10T04:30:00.000Z
     *
     * when the browser is in IST.
     *
     * This prevents the AWS/Node server timezone from
     * changing the intended meeting time.
     */

    const isoDateTime = convertToISOString(formData.dateTime);

    if (!isoDateTime) {
      showModal("Validation Error", "Invalid meeting date and time.", "error");
      return;
    }

    const payload = {
      meetingAgenda: formData.meetingAgenda.trim(),
      dateTime: isoDateTime,
      participants: formData.participants,
      description: formData.description.trim(),
      createdBy: username || formData.createdBy,
    };

    console.log("Meeting payload:", payload);

    try {
      let response;

      if (isEditMode && editId) {
        response = await axios.put(`${updateMeetingUrl}/${editId}`, payload, {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.status === 200) {
          showModal("Success", "Meeting updated successfully!", "success");
        }
      } else {
        response = await axios.post(addMeetingUrl, payload, {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.status === 200) {
          showModal("Success", "Meeting created successfully!", "success");
        }
      }

      await fetchMeetings();
      closeFormModal();
    } catch (error) {
      console.error("Error saving meeting:", error);

      showModal(
        "Error",
        error.response?.data?.message || "Failed to save meeting.",
        "error",
      );
    }
  };

  // ============================================================
  // DELETE
  // ============================================================

  const handleDelete = (_id, meetingAgenda) => {
    if (!canManage) {
      showModal(
        "Access Denied",
        "Only Admin and Accountant can delete meetings.",
        "error",
      );
      return;
    }

    setDeleteModal({
      isOpen: true,
      recordId: _id,
      meetingAgenda,
    });
  };

  const confirmDelete = async () => {
    try {
      const response = await axios.delete(
        `${deleteMeetingUrl}/${deleteModal.recordId}`,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (response.status === 200) {
        showModal("Success", "Meeting deleted successfully!", "success");

        await fetchMeetings();
      }
    } catch (error) {
      console.error("Error deleting meeting:", error);

      showModal(
        "Error",
        error.response?.data?.message || "Failed to delete meeting.",
        "error",
      );
    }

    setDeleteModal({
      isOpen: false,
      recordId: null,
      meetingAgenda: "",
    });
  };

  // ============================================================
  // CLICK OUTSIDE SUGGESTIONS
  // ============================================================

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionRef.current &&
        !suggestionRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // ============================================================
  // GET USER DISPLAY NAME
  // ============================================================

  const getUserDisplay = (participant) => {
    /*
     * Backend can return:
     *
     * "Shree"
     *
     * OR:
     *
     * {
     *   username: "Shree",
     *   email: "shree@gmail.com"
     * }
     */

    const participantUsername =
      typeof participant === "string" ? participant : participant?.username;

    if (!participantUsername) {
      return "";
    }

    if (!Array.isArray(users) || users.length === 0) {
      return participantUsername;
    }

    const user = users.find((u) => u.username === participantUsername);

    return user ? user.fullName || user.username : participantUsername;
  };

  // ============================================================
  // FORMAT DISPLAY DATE/TIME
  // ============================================================

  const formatMeetingDateTime = (dateTime) => {
    if (!dateTime) return "-";

    const date = new Date(dateTime);

    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="meeting">
      <div className="meetings-container">
        {/* ALERT MODAL */}
        <AlertModal
          isOpen={modalConfig.isOpen}
          onClose={closeModal}
          title={modalConfig.title}
          message={modalConfig.message}
          type={modalConfig.type}
        />

        <div className="meeting-container">
          {/* HEADER */}
          <div className="meeting-header">
            {canManage && (
              <button className="create-meeting-btn" onClick={openCreateModal}>
                <span className="plus-icon">+</span>
                Create New Meeting
              </button>
            )}
          </div>

          {/* TABLE */}
          <div className="table-wrapper" id="results">
            {isLoading && (
              <div className="modal-spinner-overlay">
                <div className="spinner"></div>
                <span>Loading meetings...</span>
              </div>
            )}

            {!isLoading && (
              <div className="scrollable-table">
                <table id="resultsTable">
                  <thead>
                    <tr>
                      <th>Agenda</th>
                      <th>Date & Time</th>
                      <th>Participants</th>
                      <th>Created By</th>

                      {canManage && <th>Actions</th>}
                    </tr>
                  </thead>

                  <tbody>
                    {meetings.length === 0 ? (
                      <tr>
                        <td colSpan={canManage ? 5 : 4} className="empty-row">
                          {canManage
                            ? "No meetings found. Click 'Create New Meeting' to get started."
                            : "You are not part of any meetings yet."}
                        </td>
                      </tr>
                    ) : (
                      meetings.map((meeting) => (
                        <tr key={meeting._id}>
                          <td>{meeting.meetingAgenda}</td>

                          <td>{formatMeetingDateTime(meeting.dateTime)}</td>

                          <td>
                            <div className="participant-badge-list">
                              {meeting.participants?.map(
                                (participant, index) => (
                                  <span
                                    key={
                                      typeof participant === "string"
                                        ? participant
                                        : `${participant.username}-${index}`
                                    }
                                    className="participant-badge"
                                  >
                                    {getUserDisplay(participant)}
                                  </span>
                                ),
                              )}
                            </div>
                          </td>

                          <td>{getUserDisplay(meeting.createdBy)}</td>

                          {canManage && (
                            <td>
                              <button
                                className="edit-btn"
                                onClick={() => openEditModal(meeting)}
                              >
                                Edit
                              </button>

                              <button
                                className="delete-btn"
                                onClick={() =>
                                  handleDelete(
                                    meeting._id,
                                    meeting.meetingAgenda,
                                  )
                                }
                              >
                                Delete
                              </button>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* ====================================================== */}
        {/* CREATE / EDIT MODAL */}
        {/* ====================================================== */}

        {isModalOpen && (
          <div className="meeting-modal-overlay" onClick={closeFormModal}>
            <div className="meeting-modal" onClick={(e) => e.stopPropagation()}>
              <div className="meeting-modal-header">
                <h2>{isEditMode ? "Edit Meeting" : "Create New Meeting"}</h2>

                <button className="modal-close-btn" onClick={closeFormModal}>
                  ×
                </button>
              </div>

              <form className="meeting-modal-form" onSubmit={handleFormSubmit}>
                {/* AGENDA */}
                <div className="form-group">
                  <label htmlFor="modal-meetingAgenda">Meeting Agenda *</label>

                  <input
                    type="text"
                    id="modal-meetingAgenda"
                    name="meetingAgenda"
                    value={formData.meetingAgenda}
                    onChange={handleInputChange}
                    placeholder="Enter meeting agenda"
                    required
                  />
                </div>

                {/* DATE TIME */}
                <div className="form-group">
                  <label htmlFor="modal-dateTime">Date & Time *</label>

                  <input
                    type="datetime-local"
                    id="modal-dateTime"
                    name="dateTime"
                    value={formData.dateTime}
                    onChange={handleInputChange}
                    required
                  />

                  <small
                    style={{
                      color: "#666",
                      marginTop: "4px",
                    }}
                  >
                    Meeting time is based on your local timezone.
                  </small>
                </div>

                {/* PARTICIPANTS */}
                <div className="form-group">
                  <label>Participants *</label>

                  <div
                    className="participant-input-container"
                    style={{
                      position: "relative",
                    }}
                  >
                    {formData.participants.map((participant) => (
                      <span key={participant} className="participant-tag">
                        {getUserDisplay(participant)}

                        <span
                          className="remove-tag"
                          onClick={() => removeParticipant(participant)}
                        >
                          ×
                        </span>
                      </span>
                    ))}

                    <input
                      type="text"
                      className="participant-input"
                      value={participantInput}
                      onChange={handleParticipantInputChange}
                      placeholder="Type to search users..."
                      onFocus={() =>
                        participantInput.length > 0 && setShowSuggestions(true)
                      }
                    />

                    {showSuggestions && suggestions.length > 0 && (
                      <div
                        className="participant-suggestions"
                        ref={suggestionRef}
                      >
                        {suggestions.map((user) => (
                          <div
                            key={user.username}
                            className="suggestion-item"
                            onClick={() => addParticipant(user)}
                          >
                            <span>
                              <span className="username">{user.username}</span>

                              {user.fullName &&
                                user.fullName !== user.username && (
                                  <span className="fullname">
                                    {" "}
                                    ({user.fullName})
                                  </span>
                                )}
                            </span>

                            <span className="profile-badge">
                              {user.profile}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <small
                    style={{
                      color: "#666",
                      marginTop: "4px",
                    }}
                  >
                    {formData.participants.length} participant(s) selected
                  </small>
                </div>

                {/* DESCRIPTION */}
                <div className="form-group">
                  <label htmlFor="modal-description">Description / Notes</label>

                  <textarea
                    id="modal-description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Add any additional notes about this meeting..."
                    rows="3"
                  />
                </div>

                {/* BUTTONS */}
                <div className="modal-form-actions">
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={closeFormModal}
                  >
                    Cancel
                  </button>

                  <button type="submit" className="submit-btn">
                    {isEditMode ? "Update Meeting" : "Create Meeting"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ====================================================== */}
        {/* DELETE MODAL */}
        {/* ====================================================== */}

        {deleteModal.isOpen && (
          <div className="delete-modal-overlay">
            <div className="delete-modal">
              <div className="delete-modal-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="54"
                  height="54"
                >
                  <path fill="none" d="M0 0h24v24H0z" />

                  <path
                    fill="#ff4757"
                    d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-11.414L9.172 7.757 7.757 9.172 10.586 12l-2.829 2.828 1.415 1.415L12 13.414l2.828 2.829 1.415-1.415L13.414 12l2.829-2.828-1.415-1.415L12 10.586z"
                  />
                </svg>
              </div>

              <h3>Confirm Delete</h3>

              <p>
                Are you sure you want to delete this meeting?
                <br />
                <b>Agenda:</b> {deleteModal.meetingAgenda}
              </p>

              <p className="warning-text">This action cannot be undone.</p>

              <div className="delete-modal-buttons">
                <button
                  className="cancel-btn"
                  onClick={() =>
                    setDeleteModal({
                      isOpen: false,
                      recordId: null,
                      meetingAgenda: "",
                    })
                  }
                >
                  Cancel
                </button>

                <button className="confirm-delete-btn" onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Meetings;
